import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { getMemorialColor } from '../../services/legacyChainEngine';

const { FiX, FiUser, FiLink, FiCalendar, FiGitBranch, FiInfo } = FiIcons;

export default function LegacyChainNodePanel({ node, connectedNodes, onClose }) {
  if (!node) return null;
  const color = getMemorialColor(node.memorialId);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 30 }}
        className="absolute top-4 right-4 w-72 bg-white rounded-[28px] border-2 shadow-2xl overflow-hidden z-20"
        style={{ borderColor: color.primary }}
      >
        {/* Header */}
        <div className="p-5 text-white" style={{ background: color.primary }}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              {node.avatar ? (
                <img src={node.avatar} alt={node.name}
                  className="w-12 h-12 rounded-2xl object-cover border-2 border-white/30 flex-shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center border-2 border-white/30 flex-shrink-0"
                  style={{ background: color.accent }}>
                  <span className="text-white font-black text-base font-serif">
                    {node.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                  </span>
                </div>
              )}
              <div className="min-w-0">
                <h3 className="font-serif text-sm font-bold leading-snug truncate">{node.name}</h3>
                <p className="text-[9px] font-black uppercase tracking-widest opacity-75 mt-0.5">{node.relation}</p>
              </div>
            </div>
            <button onClick={onClose}
              className="p-1.5 rounded-xl hover:bg-white/20 transition-colors flex-shrink-0">
              <SafeIcon icon={FiX} className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Memorial badge */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest"
            style={{ background: color.light, color: color.primary }}>
            <SafeIcon icon={FiLink} className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{node.memorialName}</span>
          </div>

          {/* Dates */}
          {(node.birthYear || node.deathYear) && (
            <div className="flex items-center gap-2 text-[11px] text-text-muted">
              <SafeIcon icon={FiCalendar} className="w-3.5 h-3.5 flex-shrink-0 text-accent-gold" />
              <span className="font-mono">
                {node.birthYear || '?'}
                {node.isDeceased && ` – ${node.deathYear || '?'}`}
              </span>
              {node.isDeceased && (
                <span className="ml-1 text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                  Deceased
                </span>
              )}
            </div>
          )}

          {/* Bio */}
          {node.bio && (
            <div className="flex items-start gap-2">
              <SafeIcon icon={FiInfo} className="w-3.5 h-3.5 flex-shrink-0 text-text-muted mt-0.5" />
              <p className="text-[11px] text-text-muted leading-relaxed font-light">{node.bio}</p>
            </div>
          )}

          {/* Connected nodes */}
          {connectedNodes.length > 0 && (
            <div>
              <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mb-2 flex items-center gap-1.5">
                <SafeIcon icon={FiGitBranch} className="w-3 h-3" /> Connections ({connectedNodes.length})
              </p>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {connectedNodes.map(({ node: cn, edge }) => {
                  const cnColor = getMemorialColor(cn.memorialId);
                  return (
                    <div key={cn.id}
                      className="flex items-center gap-2.5 p-2 rounded-xl border border-brand-dark bg-brand-light/50">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-[9px] font-black flex-shrink-0"
                        style={{ background: cnColor.primary }}>
                        {cn.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-bold text-accent-navy truncate">{cn.name}</p>
                        <p className="text-[8px] text-text-muted font-black uppercase tracking-widest">
                          {edge.type.replace('_', ' ')}
                          {edge.crossMemorial && ' · cross-memorial'}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}